import BrainboxPort from '../lib/brainbox_port.js';
import { safePushEvent } from '../lib/safe_push.js';
import { setCredentials, socketNeedsReconnect } from '../lib/auth_state.js';

/**
 * ExtensionBridge hook for Chrome extension communication.
 */

const AUTH_ERRORS = {
  EXTENSION_NOT_AVAILABLE: {
    error: 'Extension Not Available',
    details: 'Brainbox must be opened from inside Wavebox'
  },
  CONNECTION_TIMEOUT: {
    error: 'Connection Timeout',
    details: 'Extension did not respond in time. Please ensure Wavebox is running.'
  },
  CONNECTION_FAILED: {
    error: 'Connection Failed',
    details: 'Failed to establish connection with Wavebox extension'
  },
  NOT_AUTHENTICATED: {
    error: 'Not Authenticated',
    details: 'User is not logged into Wavebox'
  },
  CONNECTION_LOST: {
    error: 'Connection Lost',
    details: 'Connection to Wavebox extension was lost'
  }
};

const ExtensionBridge = {
  mounted() {
    this.extensionId = this.el.dataset.extensionId;
    this.connectionTimeoutMs = parseInt(this.el.dataset.connectionTimeout || '5000', 10);
    this.connectionTimeout = null;
    this.isMounted = true; // Add flag to track if still mounted

    // Reconnection settings
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.baseReconnectDelay = 1000; // 1 second
    this.maxReconnectDelay = 30000; // 30 seconds
    this.reconnectTimeout = null;
    this.wasConnected = false;

    // Store auth credentials for reconnection
    // Read hostId from URL parameters (optional)
    const params = new URLSearchParams(window.location.search);
    this.hostId = params.get('hostId'); // May be null

    // Register port event listeners once during mount
    // These will be cleaned up in destroyed()
    this.portConnectedHandler = this.handlePortConnected.bind(this);
    this.portDisconnectedHandler = this.handlePortDisconnected.bind(this);
    this.selectionChangedHandler = this.handleSelectionChanged.bind(this);
    this.tabUpdatedHandler = this.handleTabUpdated.bind(this);
    this.boundTabClosedHandler = this.handleBoundTabClosed.bind(this);
    this.startNewAssistantChatHandler = this.handleStartNewAssistantChat.bind(this);
    BrainboxPort.on('port-connected', this.portConnectedHandler);
    BrainboxPort.on('port-disconnected', this.portDisconnectedHandler);
    BrainboxPort.on('selection_changed', this.selectionChangedHandler);
    BrainboxPort.on('tab_updated', this.tabUpdatedHandler);
    BrainboxPort.on('bound_tab_closed', this.boundTabClosedHandler);
    BrainboxPort.on('start_new_assistant_chat', this.startNewAssistantChatHandler);

    if (BrainboxPort.isConnected && window.__brainboxAuth) {
      this.wasConnected = true;
      this.handlePortConnected(window.__brainboxAuth);
    } else {
      this.connect();
    }

    this.handleEvent('request_page_content', () => this.getPageContent());
    this.handleEvent('request_selection', () => this.getSelection());
    this.handleEvent('request_recent_tabs', () => {
      console.log('[ExtensionBridge] request_recent_tabs event received');
      this.getRecentTabs();
    });
    this.handleEvent('request_active_tab', () => this.getActiveTab());
    this.handleEvent('request_tab_list', () => this.getTabList());
    this.handleEvent('fetch_tab_content', (data) => this.fetchTabContent(data.attachment_id, data.tab_id));
    this.handleEvent('fetch_selection_content', (data) => this.fetchSelectionContent(data.attachment_id, data.tab_id));
    this.handleEvent('request_window_tabs', () => this.getWindowTabs());
    this.handleEvent('notify_chat_changed', (data) => this.notifyChatChanged(data.chat_id));
    this.handleEvent('save_as_smart_note', (data) => this.saveAsSmartNote(data));

    // Tool calling support
    this.handleEvent('execute_tool', (data) => this.executeTool(data));
    this.handleEvent('request_tool_catalog', () => this.getToolCatalog());

    // "Choose a browser to import" hands the user over to Wavebox's own import
    // UI — profile enumeration and the macOS App Data permission flow are native
    // concerns the add-space panel already handles, so there's nothing to run
    // here. The extension raises the request; the Wavebox sidebar opens the
    // panel on its import step.
    this.handleEvent('start_browser_import', () => this.startBrowserImport());

    // Handle conversion request from server
    this.handleEvent('convert_old_chat', async ({ chat_id }) => {
      try {
        const converted = await BrainboxPort.convertOldChat(chat_id);

        // Send converted chat back with original chat_id for tracking
        safePushEvent(this,'old_chat_converted', {
          chat_id: chat_id,
          messages: converted.messages
        });
      } catch (error) {
        console.error('[ExtensionBridge] Failed to convert chat:', error);
        safePushEvent(this,'extension_error', {
          error: 'Failed to migrate chat',
          details: error.message
        });
      }
    });

    // Handle request for fresh context when creating a new chat
    this.handleEvent('request_new_chat_context', async () => {
      try {
        // Fetch fresh active tab and selection from extension
        const context = await BrainboxPort.request('getInitialContext', {
          hostId: this.hostId || undefined
        });

        // Send back context without oldChats (we don't want to re-process those).
        //
        // `onboarding` and `features` MUST both ride along: the server reuses
        // handle_initial_context for this event and re-reads them from the
        // payload. Send one without the other and the missing one is parsed from
        // an absent key and falls back to its default — which is how a new chat
        // used to silently turn the browser-import action back off.
        safePushEvent(this,'new_chat_context_received', {
          activeTab: context?.activeTab || null,
          selection: context?.selection || null,
          onboarding: context?.onboarding || null,
          features: context?.features || null
        });
      } catch (error) {
        console.error('[ExtensionBridge] Failed to get new chat context:', error);
        // Send empty context so the flow continues
        safePushEvent(this,'new_chat_context_received', {
          activeTab: null,
          selection: null
        });
      }
    });

    // Handle delete request from server
    this.handleEvent('delete_old_chat_from_storage', async ({ chat_id }) => {
      try {
        await BrainboxPort.deleteOldChat(chat_id);

        // Notify server that chat was deleted
        safePushEvent(this,'old_chat_deleted', {
          chat_id: chat_id
        });
      } catch (error) {
        console.error('[ExtensionBridge] Failed to delete chat:', error);
        safePushEvent(this,'extension_error', {
          error: 'Failed to delete chat',
          details: error.message
        });
      }
    });
  },

  connect() {
    if (typeof chrome !== 'undefined' && chrome.runtime) {
      try {
        // Event listeners are now registered once in mounted(), not here
        BrainboxPort.connect(this.extensionId);

        this.connectionTimeout = setTimeout(() => {
          if (!this.isMounted) return; // Check if still mounted before calling pushEvent
          safePushEvent(this,'extension_error', AUTH_ERRORS.CONNECTION_TIMEOUT);
        }, this.connectionTimeoutMs);
      } catch (error) {
        console.error('Failed to connect to extension:', error);
        safePushEvent(this,'extension_error', {
          ...AUTH_ERRORS.CONNECTION_FAILED,
          details: `${AUTH_ERRORS.CONNECTION_FAILED.details}: ${error.message}`
        });
      }
    } else {
      safePushEvent(this,'extension_error', AUTH_ERRORS.EXTENSION_NOT_AVAILABLE);
    }
  },

  handlePortConnected(authData) {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    // Reset reconnection attempts on successful connection
    this.reconnectAttempts = 0;
    this.wasConnected = true;

    // Credentials only reach the server as socket connect params, so a pair the
    // current connection isn't already carrying needs a socket cycle to take
    // effect. That is rare — a token refresh, or a different signed-in user.
    //
    // It is NOT the normal launch: app.js awaits these same credentials before
    // it connects, and this hook cannot run until the LiveView has joined, so by
    // the time we get here the socket is already authenticated with them.
    // Cycling unconditionally (as this used to) dropped a healthy connection on
    // every launch, which surfaced to the user as the "Attempting to reconnect"
    // banner and cost a duplicate mount plus a duplicate Wavebox API validation.
    if (window.liveSocket && socketNeedsReconnect(authData)) {
      console.log('[ExtensionBridge] New credentials, reconnecting liveSocket');
      // Update first: the params closure reads these when building the new
      // connect URL, so without this the reconnect would resend the old pair.
      setCredentials(authData);
      // Keep the window global in step. app.js writes it once, from a one-shot
      // listener, so it would otherwise still hold the superseded pair — and
      // mounted() replays it into this same handler, which would cycle the
      // socket straight back onto the old credentials.
      window.__brainboxAuth = authData;
      window.liveSocket.disconnect();
      setTimeout(() => {
        window.liveSocket.connect();
      }, 100);
    }

    this.getInitialContext();
  },

  handlePortDisconnected(wasConnected) {
    if (wasConnected && this.wasConnected) {
      console.log('[ExtensionBridge] Connection lost, attempting to reconnect...');
      this.attemptReconnect();
    }
  },

  attemptReconnect() {
    if (!this.isMounted) {
      console.log('[ExtensionBridge] Hook unmounted, skipping reconnection');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[ExtensionBridge] Max reconnection attempts reached');
      safePushEvent(this,'extension_error', {
        ...AUTH_ERRORS.CONNECTION_LOST,
        details: 'Connection lost and failed to reconnect after multiple attempts. Please refresh the page.'
      });
      return;
    }

    // Calculate exponential backoff delay with jitter
    const exponentialDelay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay
    );
    // Add random jitter (±20%) to prevent thundering herd
    const jitter = exponentialDelay * 0.2 * (Math.random() - 0.5);
    const delay = Math.floor(exponentialDelay + jitter);

    this.reconnectAttempts++;

    console.log(
      `[ExtensionBridge] Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`
    );

    // Clear any existing reconnect timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    // Schedule reconnection
    this.reconnectTimeout = setTimeout(() => {
      if (!this.isMounted) return;

      console.log('[ExtensionBridge] Attempting to reconnect...');
      this.connect();
    }, delay);
  },

  reconnected() {
    this.getInitialContext();
  },

  async getPageContent() {
    try {
      const content = await BrainboxPort.request('getPageContent');
      safePushEvent(this,'page_content_received', content);
    } catch (error) {
      console.error('Failed to get page content:', error);
      safePushEvent(this,'page_content_received', {
        type: 'error',
        message: error.message
      });
    }
  },

  async getSelection() {
    try {
      const selection = await BrainboxPort.request('getSelection');
      console.log('[ExtensionBridge] getSelection received:', selection);
      safePushEvent(this,'selection_received', selection);
    } catch (error) {
      // No selection is a normal state, not an error
      safePushEvent(this,'selection_received', null);
    }
  },

  async getActiveTab() {
    try {
      const tab = await BrainboxPort.request('getActiveTab');
      safePushEvent(this,'active_tab_received', tab || null);
    } catch (error) {
      console.error('Failed to get active tab:', error);
      safePushEvent(this,'active_tab_received', null);
    }
  },

  async getInitialContext() {
    if (!this.liveSocket.isConnected()) {
      return;
    }
    try {
      // Include hostId in request for pre-fetched context (if available)
      const context = await BrainboxPort.request('getInitialContext', {
        hostId: this.hostId || undefined
      });

      // Push the full initial context (may include skillExecution)
      // LiveView will detect skillExecution and trigger skill execution after processing context
      safePushEvent(this,'initial_context_received', context);
    } catch (error) {
      console.error('[ExtensionBridge] Failed to get initial context:', error);
      // Send empty context so old_chats gets initialized
      safePushEvent(this,'initial_context_received', { oldChats: [] });
    }
  },

  async getRecentTabs() {
    if (!this.liveSocket.isConnected()) {
      return;
    }
    try {
      const tabs = await BrainboxPort.request('getRecentTabs', { limit: 10 });
      safePushEvent(this,'recent_tabs_received', { tabs: tabs || [] });
    } catch (error) {
      console.error('Failed to get recent tabs:', error);
      safePushEvent(this,'recent_tabs_received', { tabs: [] });
    }
  },

  async getTabList() {
    try {
      const data = await BrainboxPort.request('getTabList');
      safePushEvent(this,'tab_list_received', { tabs: data.tabs || [] });
    } catch (error) {
      console.error('Failed to get tab list:', error);
      safePushEvent(this,'tab_list_received', { tabs: [] });
    }
  },

  async getWindowTabs() {
    try {
      const data = await BrainboxPort.request('getWindowTabs');
      safePushEvent(this,'window_tabs_received', { tabs: data.tabs || [] });
    } catch (error) {
      console.error('Failed to get window tabs:', error);
      safePushEvent(this,'window_tabs_received', { tabs: [] });
    }
  },

  async fetchTabContent(attachmentId, tabId) {
    try {
      const data = await BrainboxPort.request('getTabContent', { tabId });
      safePushEvent(this,'tab_content_received', {
        attachment_id: attachmentId,
        content: data.content || '',
        content_type: data.contentType || 'text'
      });
    } catch (error) {
      console.error('Failed to fetch tab content:', error);
      safePushEvent(this,'tab_content_error', {
        attachment_id: attachmentId,
        error: error.message || 'Failed to fetch content'
      });
    }
  },

  async fetchSelectionContent(attachmentId, tabId) {
    try {
      const data = await BrainboxPort.request('getSelectionContent', { tabId });
      safePushEvent(this,'selection_content_received', {
        attachment_id: attachmentId,
        content: data.content || data.text || ''
      });
    } catch (error) {
      console.error('Failed to fetch selection content:', error);
      safePushEvent(this,'selection_content_error', {
        attachment_id: attachmentId,
        error: error.message || 'Failed to fetch content'
      });
    }
  },

  async getToolCatalog() {
    try {
      const catalog = await BrainboxPort.request('getToolCatalog');
      safePushEvent(this,'tool_catalog_received', catalog || { tools: [], categories: {} });
    } catch (error) {
      console.error('Failed to get tool catalog:', error);
      safePushEvent(this,'tool_catalog_received', { tools: [], categories: {} });
    }
  },

  notifyChatChanged(chatId) {
    try {
      BrainboxPort.send('chatChanged', chatId);
    } catch (error) {
      console.error('Failed to notify chat changed:', error);
    }
  },

  async startBrowserImport() {
    try {
      await BrainboxPort.request('startBrowserImport');
    } catch (error) {
      console.error('[ExtensionBridge] Failed to start browser import:', error);
    }
  },

  async saveAsSmartNote(data) {
    try {
      await BrainboxPort.request('saveAsSmartNote', {
        chatId: data.chat_id,
        title: data.title,
        content: data.content
      });
    } catch (error) {
      console.error('[ExtensionBridge] Failed to save as smart note:', error);
    }
  },

  /**
   * Executes a tool call via the extension and returns the result to the LiveView.
   * @param {Object} data - Tool call data from LiveView
   * @param {string} data.message_id - The message ID
   * @param {string} data.tool_use_id - The tool use ID
   * @param {string} data.tool_name - The tool name to execute
   * @param {Object} data.tool_input - The tool input parameters
   */
  async executeTool(data) {
    const { message_id, tool_use_id, tool_name, tool_input } = data;

    try {
      const result = await BrainboxPort.request('toolCall', {
        toolName: tool_name,
        params: tool_input || {}
      });

      // Send result back to LiveView
      safePushEvent(this,'tool_result_received', {
        message_id: message_id,
        tool_use_id: tool_use_id,
        result: result,
        is_error: !result.success
      });
    } catch (error) {
      console.error('Tool execution failed:', error);

      // Send error result back to LiveView
      safePushEvent(this,'tool_result_received', {
        message_id: message_id,
        tool_use_id: tool_use_id,
        result: {
          success: false,
          error: error.message || 'Tool execution failed'
        },
        is_error: true
      });
    }
  },

  /**
   * Handles live selection changes from the bound tab.
   * Forwards the event to LiveView.
   */
  handleSelectionChanged(data) {
    console.log('[ExtensionBridge] Live selection changed:', data);
    safePushEvent(this,'live_selection_changed', { data });
  },

  /**
   * Handles tab updates from the bound tab.
   * Forwards the event to LiveView.
   */
  handleTabUpdated(data) {
    console.log('[ExtensionBridge] Tab updated:', data);
    safePushEvent(this,'live_tab_updated', { data });
  },

  /**
   * Handles bound tab closure notification.
   * Forwards the event to LiveView.
   */
  handleBoundTabClosed(data) {
    console.log('[ExtensionBridge] Bound tab closed:', data);
    safePushEvent(this,'bound_tab_closed', { data });
  },

  /**
   * Extension → LiveView nudge: start a fresh Wavebox Assistant chat in
   * place. Fires when the user clicks "Open Wavebox Assistant" with the
   * BRAINBOX_5_ASSISTANT flag enabled AND this side panel is already
   * open — the extension prefers this over a URL reload so other chat
   * state stays intact.
   */
  handleStartNewAssistantChat() {
    console.log('[ExtensionBridge] Starting new Wavebox Assistant chat');
    safePushEvent(this,'start_new_assistant_chat', {});
  },

  destroyed() {
    this.isMounted = false; // Set flag to indicate hook is destroyed

    // Clear all timeouts
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Remove the specific event listeners we registered in mounted()
    if (this.portConnectedHandler) {
      BrainboxPort.off('port-connected', this.portConnectedHandler);
    }
    if (this.portDisconnectedHandler) {
      BrainboxPort.off('port-disconnected', this.portDisconnectedHandler);
    }
    if (this.selectionChangedHandler) {
      BrainboxPort.off('selection_changed', this.selectionChangedHandler);
    }
    if (this.tabUpdatedHandler) {
      BrainboxPort.off('tab_updated', this.tabUpdatedHandler);
    }
    if (this.boundTabClosedHandler) {
      BrainboxPort.off('bound_tab_closed', this.boundTabClosedHandler);
    }
    if (this.startNewAssistantChatHandler) {
      BrainboxPort.off('start_new_assistant_chat', this.startNewAssistantChatHandler);
    }

    BrainboxPort.disconnect();
  }
};

export default ExtensionBridge;
